/**
 * Main application component.
 * 
 * Sets up routing, providers, and error boundaries.
 * 
 * @module App
 */

import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/layout';

// Pages
import {
  LoginPage,
  DashboardPage,
  UsersPage,
  UserDetailPage,
  BusinessesPage,
  BusinessDetailPage,
  ReportsPage,
  ReportDetailPage,
  ContentPage,
  FAQAuditsPage,
  LLMsTxtPage,
  AuditLogsPage,
  SettingsPage,
  NotFoundPage,
} from './pages';

/**
 * Application router configuration.
 */
const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'users', element: <UsersPage /> },
      { path: 'users/:id', element: <UserDetailPage /> },
      { path: 'businesses', element: <BusinessesPage /> },
      { path: 'businesses/:id', element: <BusinessDetailPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'reports/:id', element: <ReportDetailPage /> },
      { path: 'content', element: <ContentPage /> },
      { path: 'faq-audits', element: <FAQAuditsPage /> },
      { path: 'llms-txt', element: <LLMsTxtPage /> },
      { path: 'audit-logs', element: <AuditLogsPage /> },
      { path: 'settings', element: <SettingsPage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);

/**
 * Root application component.
 * 
 * Wraps the entire application with necessary providers:
 * - ErrorBoundary for catching React errors
 * - QueryClientProvider for TanStack Query
 * - AuthProvider for authentication state
 * - SettingsProvider for display preferences
 * - RouterProvider for React Router
 * 
 * @returns Application element
 */
export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SettingsProvider>
            <RouterProvider router={router} />
          </SettingsProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
